import type { Option, ProjectedRow } from '../contract.js';
import { OTHER_LABEL } from '../project.js';

/**
 * LA REPARTITION D'UN JEU EN ETAPES, ecrite une fois : les colonnes du kanban et
 * les rangees du tableau de bord (`BordView`) rangent les fiches par la meme
 * regle, donc une fiche tombe dans la meme etape dans les deux.
 *
 * LES ETAPES VIENNENT DU CATALOGUE, PAS DES DONNEES : dans son ordre, suivi des
 * valeurs hors catalogue rencontrees (le core les y joint). Deduites des lignes,
 * une etape vide disparaitrait. Une fiche multi-valuee (`row.groups`) apparait
 * dans plusieurs etapes, jamais deux fois dans la meme. UNE LIGNE NE DISPARAIT
 * JAMAIS : celle qui n'atterrit dans aucune etape tombe dans le fourre-tout
 * (`OTHER_LABEL`), cree pour l'occasion.
 */

/** Clef d'etape du fourre-tout. Prefixee : une valeur metier ne peut pas l'usurper. */
export const OTHER_KEY = 'autres:';

/** La clef d'une etape pour une valeur de catalogue (vide = le fourre-tout). */
export function cleDEtape(value: string): string {
  return value === '' ? OTHER_KEY : `valeur:${value}`;
}

export interface EtapeRepartie {
  key: string;
  option: Option;
  cards: ProjectedRow[];
  /**
   * Une etape accepte un depot si sa valeur EXISTE. Le fourre-tout (valeur
   * vide) n'en est pas une : y deposer une carte reviendrait a ecrire une
   * valeur vide dans la donnee metier, ce qu'aucun ecran n'a demande.
   */
  droppable: boolean;
}

/** Range les lignes dans les etapes du catalogue, le fourre-tout en dernier s'il sert. */
export function repartirEnEtapes(rows: readonly ProjectedRow[], catalogue: readonly Option[]): EtapeRepartie[] {
  const byKey = new Map<string, EtapeRepartie>();
  const ordered: EtapeRepartie[] = [];

  for (const option of catalogue) {
    const key = cleDEtape(option.value);
    if (byKey.has(key)) continue; // catalogue en double : une seule etape.
    const etape: EtapeRepartie = { key, option, cards: [], droppable: option.value !== '' && option.archived !== true };
    byKey.set(key, etape);
    ordered.push(etape);
  }

  const orphans: ProjectedRow[] = [];
  for (const row of rows) {
    const placed = new Set<string>();
    for (const group of row.groups) {
      const key = cleDEtape(group.value);
      if (placed.has(key)) continue;
      const etape = byKey.get(key);
      if (etape == null) continue;
      etape.cards.push(row);
      placed.add(key);
    }
    if (placed.size === 0) orphans.push(row);
  }

  // Le filet : une ligne sans etape reste visible. Ce cas ne devrait pas
  // arriver (le core joint deja les valeurs rencontrees au catalogue), mais une
  // carte perdue est un mensonge silencieux, donc on le couvre.
  if (orphans.length > 0) {
    let other = byKey.get(OTHER_KEY);
    if (other == null) {
      other = { key: OTHER_KEY, option: { value: '', label: OTHER_LABEL, tone: 'neutral' }, cards: [], droppable: false };
      byKey.set(OTHER_KEY, other);
      ordered.push(other);
    }
    other.cards.push(...orphans);
  }

  return ordered;
}
